import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import {
  Box,
  Chip,
  Grid,
  IconButton,
  Link,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { tokens } from "../../../theme";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import {
  OrderInfo,
  OrderItemDTO,

  fetchOrderById,
  getOrderItemsByOrderId,

  orderItemInvoice,
  useOrderSlice,
} from "../../../redux/slice/order/OrderSlice";
import OptionsPopup, {
  OptionPopupObjectProps,
} from "../../common/OptionsPopup";
import CustomDatePicker from "../../common/CustomDatePicker";
import CustomTextField from "../../common/CustomTextField";
import Button from "../../common/Button";
import PageBackButton from "../../common/PageBackButton";
import {
  Add,
  AddCircleOutlineOutlined,
  Cancel,
  CheckCircleOutline,
  Delete,
  Edit,
  Inventory2Outlined,
  PersonOutlined,
  ReceiptLongOutlined,
  AccountBalanceWalletOutlined,
} from "@mui/icons-material";
import CustomCheckbox from "../../common/CustomCheckBox";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { selectIsTokenAvailable } from "../../../redux/slice/authSlice";
import dayjs from "dayjs";
import { getProductItemHistory } from "../../../redux/slice/product/createProductSlice";
import { getProductItem } from "../../../service/ProductService";
import {
  getPracticeProductById,
  getPracticeProductByPracticeProductId,
  PracticeInfo,
} from "../../../redux/slice/practiceSlice";
import {
  MainErrorInfo,
  SingleAlertInfo,
  setMainErrorObj,
  setSingleAlertObj,
} from "../../../redux/slice/commonSlice";
import CustomCurrencyField from "../../common/CustomCurrencyField";
import Dropzone, { ExtendedFile } from "../../common/Dropzone";
import {
  calculateCommissionAmount,
  calculateCommissionRate,
  convertBase64,
  formatValue,
  fullName,
  getCodeTableDisplayLabel,
  hideElement,
  isV3CommissionType,
  priceFormatter,
  sortNames,
  toCommissionTypeRequestKey,
} from "../../../utils/Utility";
import {
  addInvoice,
  Attachment,
  deleteInvSpecificAgencyByInvAgencyId,
  EditInvDetail,
  getApplyInvDetailById,
  getEditInvDetailsById,
  resetEditInvoiceDetails,
  SaveInvoiceInfo,
  updateInvoice,
  useInvoiceSlice,
} from "../../../redux/slice/invoice/InvoiceSlice";
import { COMMISSION_TYPE_CODETABLE, ORDER_AVAILABILITY, RESPONSE_CODES } from "../../../constants/applicationConstants";
import { useCodetablesSlice } from "../../../redux/slice/codetableSlice";
import moment from "moment";
import { useNavigation } from "../../../hooks/useNavigation";
import CustomIconButton from "../../common/CustomIconButton";
import AddProductCommissionModal from "../agency/ProductCommissionPopup";
import {
  fetchProductsByUserRoleData,
  ProductInfo,
  useProductListReducer,
} from "../../../redux/slice/product/productListSlice";
import {
  AgencyOption,
  CreateSearchAgency,
  editAgencyProductCommission,
  fetchActiveAgencyExecutives,
  fetchAssociateAgencyList,
  getPracticeListByUserRoleInAgency,
  Option,
  ProductCommissionAgency,
  useAgencyReducer,
} from "../../../redux/slice/agency/AgencySlice";
import OVRCommissionPopup from "../orders/OvrCommissionPopup";
import EditCommissionPopup from "../orders/EditCommissionPopup";
import {
  resolvePreviousShippingCharge,
  shippingChargesEqual,
} from "../orders/shippingMethodUtils";
import { orderItemClaim, claimInfo, addClaim, reimburseClaimInfo, addReimbursedClaim } from "../../../redux/slice/claim/ClaimSlice";
import { SectionHeader } from "../../ui/ContentSection";
import { useModalStyles } from "../../common/useModalStyles";

interface Column {
  id: string;
  label: string | JSX.Element;
  minWidth?: number;
  align?: "right" | "center" | "left";
  format?: (value: number) => string;
}

export interface OrderItemRow {
  select: JSX.Element;
  orderItemId: number;
  serialNumber: string;
  size: string;
  tissueTrackingId: number;
  mfgInvoiceNumber?: string;
  productReturnId: number;
  productReturnStatus?: string;
  claim: orderItemClaim;
  invoice: orderItemInvoice;
  extendedPrice: number;
  invoiceId: number;
  units: number;
  hasSKUPrice: boolean;
}

interface billingForm {
  insuranceClaim: string;
  mfgInvoice: string;
  invoiceAmt: number;
  invoicedDate: any;
  invoiceDueDate: string;
  calculatedAmt: number;
  attachment: Attachment[];
  comments: string;
  invReferenceId?: string;
}

interface FormattedOrderItem {
  orderItemId: number;
}

export type EditInvoiceProps = {
  invoiceIdOverride?: number;
  onRequestClose?: () => void;
  hideHeader?: boolean;
  onSaved?: () => void;
  /** When true, renders only the Invoice Details fields (MFG Invoice #, amounts, dates) instead of the full edit page. */
  compact?: boolean;highlightFieldName?: string;
};

export type EditInvoiceHandle = {
  submit: () => void;
};

const EditInvoice = forwardRef<EditInvoiceHandle, EditInvoiceProps>(({ invoiceIdOverride, onRequestClose, hideHeader, onSaved, compact, highlightFieldName } = {}, ref) => {
  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    watch,
    reset,
    formState: { errors },
  } = useForm<any>({
    defaultValues: {
      invoiceId: 0,
      productId: 0,
      productName: "",
      practiceId: 0,
      practiceName: "",
      manufacturerId: 0,
      mfgName: "",
      mfgInvoiceNumber: "",
      calculatedAmount: 0,
      invoiceAmount: 0,
      mfgInvoiceDate: "",
      invoiceDueDate: "",
      orderId: 0,
      orderNumber: "",
      patientId: 0,
      patientName: "",
      invoiceCommissionDetailsList: [],
      orderItemDTOList: [],
      comments: "",
      invReferenceId: "",
    },
  });

  const { navigate } = useNavigation();
  const closeOrGoBack = () => (onRequestClose ? onRequestClose() : navigate(-1));
  const closeOrGoToInvoiceList = () => (onRequestClose ? onRequestClose() : navigate("/invoice_list"));
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const {
    tokens: mTokens,
    isDark: isDarkModal,
    datepickerWrapSx,
    inputFieldSx,
    primaryBtnSx,
    cancelBtnSx,
  } = useModalStyles();
  const cardSx = {
    borderRadius: "16px",
    border: `1px solid ${mTokens.border}`,
    backgroundColor: mTokens.bg,
    backgroundImage: "none",
    overflow: "hidden",
    boxShadow: isDarkModal ? "0 10px 20px rgba(0,0,0,0.1)" : "none",
  };
  const dispatch = useAppDispatch();
  const { allCodeTablesValuesMap } = useCodetablesSlice();
  const [order, setOrder] = useState<OrderInfo>();
  const [checkedList, setCheckedList] = useState<number[]>([]);
  const [uncheckedList, setUncheckedList] = useState<number[]>([]);
  const [showOptionPopup, setShowOptionsPopup] =
    useState<OptionPopupObjectProps>({ message: "", show: false });
  const [orderInfo, setOrderInfo] = useState<OrderInfo>();
  const [orderDetail, setOrderDetail] = useState<OrderInfo | null>(null);
  const { editProductCommissionStatus } = useOrderSlice();
  //CS-1191 - Start
  const { editInvoiceDetail } = useInvoiceSlice();
  //CS-1191 - End
  const [submitAction, setSubmitAction] = useState<string>();
  // Mirrors `submitAction` synchronously so the imperative `submit()` below always reads the
  // action it just set, instead of the stale value captured by React state before it re-renders.
  const submitActionRef = useRef<string | undefined>(undefined);

  useImperativeHandle(ref, () => ({
    submit: () => {
      submitActionRef.current = "markInvoiced";
      setSubmitAction("markInvoiced");
      setTimeout(() => handleSubmit(onSubmit)(), 0);
    },
  }));

  const [productExtendedPrice, setProductExtendedPrice] = useState<number>(0);
  const [orderItemRow, setOrderItemRow] = useState<any>([]);
  const [currentInvoiceId, setCurrentInvoiceId] = useState<any>([]);
  const [totalExtendedPrice, setTotalExtendedPrice] = useState(0);
  const [statusCount, setStatusCount] = useState<{
    orderItemsCount: number;
    countToCheck: number;
  }>({
    orderItemsCount: 0,
    countToCheck: 0,
  });
  const location = useLocation();
  const queryInvoiceId = new URLSearchParams(location.search).get("invoiceId");
  const resolvedInvoiceId =
    invoiceIdOverride ?? location?.state ?? (queryInvoiceId ? Number(queryInvoiceId) : undefined);
  //   const [extendedPriceInfo, setExtendedPriceInfo] = useState("");
  const [invoiceNetdays, setInvoiceNetdays] = useState(0);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isNarrow = useMediaQuery(theme.breakpoints.down("md"));
  const [showOvrCommissionPopup, setShowOvrCommissionPopup] = useState(false);
  const [showEditCommissionPopup, setShowEditCommissionPopup] = useState(false);
  const [agencyData, setAgencyData] = useState({});
  const [newOvrCommissionData, setNewOvrCommissionData] = useState<any>([]);
  const [newInvSpecificCommissionData, setNewInvSpecificCommissionData] =
    useState<any>([]);
  const [combinedCommissionData, setCombinedCommissionData] = useState<any[]>(
    []
  );
  const [invoiceData, setInvoiceData] = useState<EditInvDetail | null>();
  const Columns: Column[] = [
    { id: "type", label: "Type", minWidth: 100 },
    { id: "agency", label: "Agency", minWidth: 200 },
    { id: "commissionPercent", label: "Rate(%)", minWidth: 50 },
    { id: "commissionAmount", label: "Amount($)", minWidth: 50 },
    { id: "actionButtons", label: "Action", minWidth: 50 },
  ];

  // const orderGraftColumns : Column[]=[
  //     { id: "checkBox", label: <CustomCheckbox control={control} onChange={(e) => handleSelectAllCheckbox(e)} controllerName="selectAll" checked={isAllSelected} /> },
  //     { id: "orderSize", label: "Ordered Size", minWidth: 100 },
  //     { id: "serialNumber", label: "Serial Number", minWidth: 100 },
  //     { id: "status", label: "Status", minWidth: 100 },
  //     { id: "cogs", label: "COGs", minWidth: 100 },
  // ]

  useEffect(() => {
    dispatch(getEditInvDetailsById(resolvedInvoiceId));
  }, []);
  //2026-01-22: Old implementation that caused stale data in calculatedAmt field
  // The issue was that calculation happened in a separate useEffect, causing a delay
  // useEffect(() => {
  //   setInvoiceData(editInvoiceDetail);
  //   setOrderItemRow(editInvoiceDetail?.orderItemDTOList);
  //   setCurrentInvoiceId(editInvoiceDetail?.invoiceId);
  //   setValue("mfgInvoice", editInvoiceDetail?.mfgInvoiceNumber);
  //   setValue("calculatedAmt", editInvoiceDetail?.calculatedAmount);
  //   setValue("invoiceAmt", editInvoiceDetail?.invoiceAmount);
  //   setValue("invoicedDate", editInvoiceDetail?.mfgInvoiceDate);
  //   setValue("invoiceDueDate", editInvoiceDetail?.invoiceDueDate);
  //   setValue("comments", editInvoiceDetail?.comments ?? "");
  //   setValue("shippingCharge", editInvoiceDetail?.shippingCharge ?? 0);
  //   setValue("lateFee", editInvoiceDetail?.lateFee ?? 0);
  //   const customConfig = editInvoiceDetail?.manufactureCustomConfig;
  //   const invoiceNetdays =
  //     customConfig?.invoice_net_days && customConfig?.invoice_net_days > 0
  //       ? customConfig?.invoice_net_days
  //       : 0;
  //   setInvoiceNetdays(invoiceNetdays);
  //   if (editInvoiceDetail?.orderItemDTOList) {
  //     let count = 0;
  //     editInvoiceDetail?.orderItemDTOList?.map(async (orderItem: any) => {
  //       const invoicedStatus =
  //         (orderItem?.tissueTrackingId && orderItem?.invoiceId) ||
  //         (orderItem?.productReturnId && orderItem?.invoiceId) ||
  //         (!orderItem?.tissueTrackingId &&
  //           !orderItem?.productReturnId &&
  //           orderItem?.invoiceId);
  //       if (!invoicedStatus) {
  //         count += 1;
  //         setStatusCount((prev) => {
  //           return { ...prev, countToCheck: count };
  //         });
  //       }
  //       setStatusCount((prev: any) => {
  //         return {
  //           ...prev,
  //           orderItemsCount: editInvoiceDetail?.orderItemDTOList?.length,
  //         };
  //       });
  //     });
  //   }
  // }, [editInvoiceDetail]);

  //NEW IMPLEMENTATION - 2026-01-22: Combined state updates and calculation in single effect
  // This eliminates the extra render cycle and prevents stale data in calculatedAmt field
  useEffect(() => {
    setInvoiceData(editInvoiceDetail);
    const orderItems = editInvoiceDetail?.orderItemDTOList;
    setOrderItemRow(orderItems);
    setCurrentInvoiceId(editInvoiceDetail?.invoiceId);
    setValue("mfgInvoice", editInvoiceDetail?.mfgInvoiceNumber);
    setValue("orderNumber", editInvoiceDetail?.orderNumber);
    setValue("invoiceAmt", editInvoiceDetail?.invoiceAmount);
    setValue("invoicedDate", editInvoiceDetail?.mfgInvoiceDate);
    setValue("invoiceDueDate", editInvoiceDetail?.invoiceDueDate);
    setValue("comments", editInvoiceDetail?.comments ?? "");
    setValue("shippingCharge", editInvoiceDetail?.shippingCharge ?? 0);
    setValue("lateFee", editInvoiceDetail?.lateFee ?? 0);
    setValue("invReferenceId", editInvoiceDetail?.invReferenceId ?? "");
    
    const customConfig = editInvoiceDetail?.manufactureCustomConfig;
    const invoiceNetdays =
      customConfig?.invoice_net_days && customConfig?.invoice_net_days > 0
        ? customConfig?.invoice_net_days
        : 0;
    setInvoiceNetdays(invoiceNetdays);
    
    if (editInvoiceDetail?.orderItemDTOList) {
      let count = 0;
      editInvoiceDetail?.orderItemDTOList?.map(async (orderItem: any) => {
        const invoicedStatus =
          (orderItem?.tissueTrackingId && orderItem?.invoiceId) ||
          (orderItem?.productReturnId && orderItem?.invoiceId) ||
          (!orderItem?.tissueTrackingId &&
            !orderItem?.productReturnId &&
            orderItem?.invoiceId);
        if (!invoicedStatus) {
          count += 1;
          setStatusCount((prev) => {
            return { ...prev, countToCheck: count };
          });
        }
        setStatusCount((prev: any) => {
          return {
            ...prev,
            orderItemsCount: editInvoiceDetail?.orderItemDTOList?.length,
          };
        });
      });
    }

    //CALCULATE IMMEDIATELY - 2026-01-22: Calculate checked items and amount in same effect
    // This prevents the delay/flicker in calculatedAmt field
    if (orderItems && orderItems.length > 0 && editInvoiceDetail?.invoiceId) {
      const preChecked: number[] = [];
      let invAmt = 0;

      orderItems.forEach((row: any) => {
        const status = orderItemStatus(row);
        const isInvoicedStatus =
          status === "Invoiced" ||
          status === "Applied (Invoiced)" ||
          status === "Returned (Invoiced)" ||
          status === "Return Initiated (Invoiced)" ||
          status === "Return Pending (Invoiced)" ||
          status === "Return Rejected (Invoiced)";

        if (isInvoicedStatus && row.invoiceId === editInvoiceDetail.invoiceId) {
          preChecked.push(row.orderItemId);
          invAmt += row.orderItemPrice;
        }
      });

      setCheckedList(preChecked);
      setTotalExtendedPrice(invAmt);
      setValue("calculatedAmt", invAmt);
    }
  }, [editInvoiceDetail]);

  //2026-01-22: Removed separate calculation effect
  // This was causing the stale data issue because it ran in a separate render cycle
  // The calculation is now done immediately in the first useEffect above
  // useEffect(() => {
  //   if (orderItemRow?.length > 0 && currentInvoiceId) {
  //     const preChecked: number[] = [];
  //     let invAmt = 0;

  //     orderItemRow.forEach((row: any) => {
  //       const status = orderItemStatus(row);
  //       const isInvoicedStatus =
  //         status === "Invoiced" ||
  //         status === "Applied (Invoiced)" ||
  //         status === "Returned (Invoiced)" ||
  //         status === "Return Initiated (Invoiced)" ||
  //         status === "Return Rejected (Invoiced)";

  //       if (isInvoicedStatus && row.invoiceId === currentInvoiceId) {
  //         preChecked.push(row.orderItemId);
  //         //CS-1560 - Using order item price directly from backend
  //         invAmt += row.orderItemPrice;
  //         // invAmt += row.hasSKUPrice
  //         //   ? row.extendedPrice
  //         //   : row.extendedPrice * row.units;
  //       }
  //     });

  //     setCheckedList(preChecked);
  //     setTotalExtendedPrice(invAmt);
  //     setValue("calculatedAmt", invAmt);
  //   }
  // }, [orderItemRow, currentInvoiceId]);

  useEffect(() => {
    dispatch(getEditInvDetailsById(resolvedInvoiceId));
  }, [editProductCommissionStatus === "success"]);

  useEffect(() => {
    const orderId = editInvoiceDetail?.orderId;
    if (!orderId) {
      setOrderDetail(null);
      return;
    }

    const loadOrderDetail = async () => {
      const response = await dispatch(fetchOrderById({ orderId, isSaveAuditLog: false }));
      const savedOrder = response?.payload?.data;
      if (savedOrder) {
        setOrderDetail(savedOrder);
      }
    };

    loadOrderDetail();
  }, [editInvoiceDetail?.orderId]);

  const parseSize = (size: string): number => {
    const sizeWithoutCm = size.replace("cm", "");
    const [length, width] = sizeWithoutCm.split("x").map(Number);
    return length * width;
  };

  const modifyClaimObject = (
    claim: orderItemClaim,
    size: string
  ): orderItemClaim => {
    return {
      ...claim,
      claimAmount: parseSize(size) * 122,
    };
  };

  const selectedOrderItems: { orderItemId: number }[] = useMemo(() => {
    const newSelectedOrderItems: { orderItemId: number }[] = checkedList?.map(
      (id) => ({
        orderItemId: id,
      })
    );
    return newSelectedOrderItems;
  }, [checkedList]);

  const onSubmit = async (data: any) => {
    const currentSubmitAction = submitActionRef.current ?? submitAction;
    if (currentSubmitAction === "markClaim") {
      if (orderInfo) {
        const claimDtoObject: claimInfo = {
          orderId: orderInfo?.orderId,
          productId: orderInfo?.productId,
          claimNumber: data.insuranceClaim,
          claimStatus: "SUBMITTED",
          submittedDate: dayjs(new Date()).format("MM/DD/YYYY"),
          orderItems: [],
        };
        // const response = await dispatch(addClaim(claimDtoObject));

        // if (response.payload && response.payload.data.message === "SUCCESS") {
        //   let alertMessage = "Mark Claim Submitted!";
        //   const alertObj: SingleAlertInfo = {
        //     message: alertMessage,
        //     alertType: "success",
        //   };
        //   dispatch(setSingleAlertObj(alertObj));
        // }
      }
    } else if (currentSubmitAction === "markInvoiced") {
      if (checkedList?.length > 0) {
        //CS-1213 - Start
        //CS-1745 - Start: Validate submittedTotal against combined payment + credit obligations
        const submittedTotal = Number(data.invoiceAmt || 0) + Number(data.shippingCharge || 0) + Number(data.lateFee || 0);
        const existingPayment = Number(editInvoiceDetail?.payment?.totalAmount || 0);
        const existingCredit = Number(editInvoiceDetail?.creditAmount || 0);
        const combinedObligations = existingPayment + existingCredit;
        if (combinedObligations > 0 && submittedTotal < combinedObligations) {
          setShowOptionsPopup({
            message: existingPayment > 0 && existingCredit > 0
              ? `We already have a payment of $ ${existingPayment.toFixed(2)} and credit memo of $ ${existingCredit.toFixed(2)} received against this invoice. Please correct the Invoice Amount.`
              : existingPayment > 0
              ? `We already have a payment of $ ${existingPayment.toFixed(2)} received against this invoice. Please correct the Invoice Amount.`
              : `We already have a credit memo of $ ${existingCredit.toFixed(2)} received against this invoice. Please correct the Invoice Amount.`,
            show: true,
            variant: "error",
            buttons: [
              {
                name: "Cancel",
                color: "secondary",
                onClick: cancelOptionPopup,
              },
            ],
          });
        //CS-1745 - End
        } else {
          const areEqual =
            parseFloat(Number(data.calculatedAmt).toFixed(2)) ===
            parseFloat(Number(data.invoiceAmt).toFixed(2));

          setShowOptionsPopup({
            message:
              areEqual
                ? "Do you really want to update the invoice?"
                : "Calculated Amount and Invoice Amount are not equal. Do you still want to update the invoice?",
            show: true,
            variant: "confirm",
            buttons: [
              {
                name: "Confirm",
                color: "primary",
                onClick: () => submitMarkInvoiced(data),
              },
              {
                name: "Cancel",
                color: "secondary",
                onClick: cancelOptionPopup,
              },
            ],
          });
        }
        //CS-1213 - End
      } else {
        let alertMessage = "Please select atleast one order item";
        const alertObj: SingleAlertInfo = {
          message: alertMessage,
          alertType: "error",
        };
        dispatch(setSingleAlertObj(alertObj));
      }
    }
  };

  const submitMarkInvoiced = async (data: any) => {
    if (invoiceData) {
      const commissionRequests = combinedCommissionData?.map((item, index) => {
        const amount = getValues(`commissionAmt-${index}`);
        return {
          agencyId: item.agencyId,
          invoiceAgencyId: item.invoiceAgencyId || 0,
          agencyType: toCommissionTypeRequestKey(allCodeTablesValuesMap, item.agencyType),
          productCommissionId: item.productCommissionId || null,
          expectedCommissionRate: item.commissionRate || 0,
          expectedCommissionAmount: amount || 0, // fallback to 0 if empty
          commissionChangeForAllFutureInvoices:
            item.commissionChangeForAllFutureInvoices || false,
        };
      });
      const editInvoiceDtoObject: any = {
        //SaveInvoiceInfo
        invoiceId: invoiceData?.invoiceId,
        orderId: invoiceData?.orderId,
        practiceId: invoiceData?.practiceId,
        mfgInvoiceNumber: data?.mfgInvoice,
        mfgInvoiceDate: data?.invoicedDate,
        invoiceDueDate: dayjs(data?.invoiceDueDate).format("MM/DD/YYYY"),
        calculatedAmount: data.calculatedAmt,
        invoiceAmount: Number(data.invoiceAmt),
        shippingCharges: shippingCharge,
        lateFee: lateFee,
        ...(editInvoiceDetail?.purchaseAndBill === "Y"
          ? { invReferenceId: data?.invReferenceId ?? "" }
          : {}),
        orderItems: selectedOrderItems,
        attachInvoice: data.attachment,
        orderInvoiceStatus:
          orderItemRow?.length === checkedList?.length ? "INVOICED" : "PARTIAL",
        commissionRequests: commissionRequests,
        deletedOrderItemIds: uncheckedList,
        oldDocumentId:
          data.attachment?.length > 0
            ? invoiceData?.documentDto?.documentId
            : null,
        totalAmount: editInvoiceDetail?.payment?.totalAmount, //CS-1213
        comments: data.comments
      };

      const isPurchaseAndBill = editInvoiceDetail?.purchaseAndBill === "Y";
      if (
        commissionRequests?.some(
          (data: any) =>
            ((!isPurchaseAndBill && isV3CommissionType(allCodeTablesValuesMap, data?.agencyType)) ||
              data?.agencyType === "AGENCY" ||
              data?.agencyType === "OVERRIDE") &&
            data?.expectedCommissionAmount <= 0
        )
      ) {
        setShowOptionsPopup({
          message: isPurchaseAndBill
            ? "Service Fee amount for the Agency/Override Agency is missing or zero. Do you still want to proceed with updating the invoice?"
            : `Service Fee amount for the ${getCodeTableDisplayLabel(allCodeTablesValuesMap, COMMISSION_TYPE_CODETABLE, "V3")}/Agency/Override Agency is missing or zero. Do you still want to proceed with updating the invoice?`,
          show: true,
          variant: "confirm",
          buttons: [
            {
              name: "Confirm",
              color: "primary",
              onClick: async () => {
                const response = await dispatch(
                  updateInvoice({
                    interceptorOption: { skipErrorPopup: true },
                    data: editInvoiceDtoObject,
                  })
                );
                if (response?.payload?.code === RESPONSE_CODES.SUCCESS_CODE) {
                  let alertMessage = "Invoice was updated successfully";
                  const alertObj: SingleAlertInfo = {
                    message: alertMessage,
                    alertType: "success",
                  };
                  dispatch(setSingleAlertObj(alertObj));
                  onSaved?.();
                  closeOrGoToInvoiceList();
                  cancelOptionPopup();
                } else {
                  const alertObj: SingleAlertInfo = {
                    message: response?.payload?.message || "Unable to update the invoice. Please try again.",
                    alertType: "error",
                  };
                  dispatch(setSingleAlertObj(alertObj));
                }
                cancelOptionPopup();
              },
            },
            { name: "Cancel", color: "secondary", onClick: cancelOptionPopup },
          ],
        });
      } else {
        const response = await dispatch(
          updateInvoice({
            interceptorOption: { skipErrorPopup: true },
            data: editInvoiceDtoObject,
          })
        );
        if (response?.payload?.code === RESPONSE_CODES.SUCCESS_CODE) {
          let alertMessage = "Invoice was updated successfully";
          const alertObj: SingleAlertInfo = {
            message: alertMessage,
            alertType: "success",
          };
          dispatch(setSingleAlertObj(alertObj));
          onSaved?.();
          closeOrGoBack();
        } else {
          const alertObj: SingleAlertInfo = {
            message: response?.payload?.message || "Unable to update the invoice. Please try again.",
            alertType: "error",
          };
          dispatch(setSingleAlertObj(alertObj));
        }
        cancelOptionPopup();
      }
    }
  };

  const cancelOptionPopup = () => {
    setShowOptionsPopup({ message: "", show: false });
  };

  const claimDtoObject: orderItemClaim = {
    claimId: 2,
    claimNumber: "C3156456",
    claimAmount: 22.36,
    claimStatus: "REIMBURSED",
    submittedDate: "05/22/2024",
    reimbursedDate: "06/13/2024",
  };

  //   useEffect(() => {
  //     const fetchOrderItems = async () => {
  //       if (orderItemsListInfo) {
  //         let count = 0;
  //         const orderItemsPromises = orderItemsListInfo.map(async (orderItem) => {
  //           const response = await dispatch(
  //             getPracticeProductByPracticeProductId({practiceProductId: orderItem.practiceProductId, productItemId: orderItem.productItemId.toString()})
  //           );
  //           const invoicedStatus = (orderItem?.tissueTrackingId && orderItem?.invoiceId) || (orderItem?.productReturnId && orderItem?.invoiceId) || (!orderItem?.tissueTrackingId && !orderItem?.productReturnId && orderItem?.invoiceId);
  //           if (
  //             !invoicedStatus
  //           ) {
  //             count += 1;
  //             setStatusCount((prev) => {
  //               return { ...prev, countToCheck: count };
  //             });
  //           }
  //           return {
  //             select: <CustomCheckbox control={control} name="select" />,
  //             orderItemId: orderItem.orderItemId,
  //             serialNumber: orderItem.serialNumber,
  //             size: orderItem.size,
  //             tissueTrackingId: orderItem.tissueTrackingId,
  //             productReturnId: orderItem.productReturnId,
  //             claim: modifyClaimObject(claimDtoObject, orderItem.size),
  //             invoice: orderItem.invoice,
  //             extendedPrice: response?.payload?.extendedPrice
  //               ? response.payload.extendedPrice
  //               : "",
  //             productItemId: orderItem.productItemId,
  //             invoiceId: orderItem.invoiceId,
  //             units: orderItem.units,
  //             hasSKUPrice: orderItem.hasSKUPrice,
  //           };
  //         });
  //         const orderItems = (await Promise.all(orderItemsPromises)).sort((a, b) => a.units - b.units);

  //         // const extendedPrices = orderItems
  //         //   .map((item) => item.extendedPrice)
  //         //   .filter((price) => price !== "");
  //         // const firstArrayPrice = extendedPrices[0] || "";
  //         // setExtendedPriceInfo(firstArrayPrice);

  //         setOrderItemRow(orderItems);
  //         setStatusCount((prev) => {
  //           return { ...prev, orderItemsCount: orderItemsListInfo.length };
  //         });
  //       }
  //     };

  //     fetchOrderItems();
  //   }, [orderItemsListInfo]);

  //   const handleCheckboxChange = (
  //     e: any,
  //     orderItemId: number,
  //     extendedPrice: number,
  //     units: number,
  //     hasSKUPrice: boolean
  //   ) => {
  //     const { checked } = e.target;

  //     setTotalExtendedPrice((prev) => {
  //       const newVal = checked
  //         ? prev + (hasSKUPrice ? extendedPrice : extendedPrice * units)
  //         : prev - (hasSKUPrice ? extendedPrice : extendedPrice * units);
  //       setValue("calculatedAmt", newVal);
  //       return newVal;
  //     });

  //     setCheckedList((prev) => {
  //       if (!prev.includes(orderItemId)) {
  //         return [...prev, orderItemId];
  //       }
  //       return prev.filter((id) => id !== orderItemId);
  //     });
  //   };

  const handleCheckboxChange = (
    e: any,
    orderItemId: number,
    extendedPrice: number,
    units: number,
    hasSKUPrice: boolean,
    orderItemPrice: number //CS-1560 - Adding order item price directly from backend
  ) => {
    const { checked } = e.target;

    const valueToChange = hasSKUPrice ? extendedPrice : extendedPrice * units;
    //CS-1560 - Start
    setTotalExtendedPrice((prev) => {
      // const newVal = checked ? prev + valueToChange : prev - valueToChange;
      const newVal = checked ? prev + orderItemPrice : prev - orderItemPrice;
      setValue("calculatedAmt", newVal);
      return newVal;
    });

    setCheckedList((prev) => {
      if (checked) {
        setUncheckedList((uPrev) => uPrev.filter((id) => id !== orderItemId));
        if (!prev.includes(orderItemId)) {
          return [...prev, orderItemId];
        }
        return prev;
      } else {
        setUncheckedList((uPrev) => {
          if (!uPrev.includes(orderItemId)) return [...uPrev, orderItemId];
          return uPrev;
        });
        return prev.filter((id) => id !== orderItemId);
      }
    });
  };

  const handleMarkedClaimReimbursed = async (claim: orderItemClaim) => {
    const addReimbursedClaimObj: reimburseClaimInfo = {
      claimId: claim.claimId,
      claimAmount: claim.claimAmount ? claim.claimAmount : 0,
      claimStatus: "REIMBURSED",
      reimbursedDate: dayjs(new Date()).format("MM/DD/YYYY"),
    };
    const response = await dispatch(addReimbursedClaim(addReimbursedClaimObj));
  };

  const handleMarkedInvoicePaid = (orderItemId: number) => {
    // setIsInvoicePaid((prevStatuses) => ({
    //   ...prevStatuses,
    //   [orderItemId]: !prevStatuses[orderItemId],
    // }));
  };

  const orderItemStatus = useCallback((row: OrderItemRow) => {
    if (row?.tissueTrackingId) {
      if (row?.invoiceId) {
        return "Applied (Invoiced)";
      }
      return "Applied";
    } else if (row?.productReturnId) {
      if (row.productReturnStatus === ORDER_AVAILABILITY.RETURN_INITIATED) {
        if (row?.invoiceId) {
          return "Return Initiated (Invoiced)";
        }
        return "Return Initiated";
      }
      if (row.productReturnStatus === ORDER_AVAILABILITY.RETURN_PENDING) {
        if (row?.invoiceId) {
          return "Return Pending (Invoiced)";
        }
        return "Return Pending";
      }
      if (row.productReturnStatus === ORDER_AVAILABILITY.RETURN_REJECTED) {
        if (row?.invoiceId) {
          return "Return Rejected (Invoiced)";
        }
        return `Available (Return Rejected)`;
      }
      if (row.productReturnStatus === ORDER_AVAILABILITY.RETURNED) {
        if (row?.invoiceId) {
          return "Returned (Invoiced)";
        }
        return `Returned`;
      }
    } else if (row?.invoiceId) {
      return "Invoiced";
    }
    return "Available";
  }, []);
  //CS-1191 - Start
  const getStatusChipSx = (status: string) => {
    if (status?.includes("Applied")) return { backgroundColor: "rgba(20,184,166,0.15)", color: "#14b8a6", border: "1px solid rgba(20,184,166,0.4)", fontWeight: 700 };
    if (status === "Available" || status?.includes("Available")) return { backgroundColor: "rgba(40,167,69,0.15)", color: "#28a745", border: "1px solid rgba(40,167,69,0.4)", fontWeight: 700 };
    if (status?.includes("Return Initiated")) return { backgroundColor: "rgba(0,184,217,0.15)", color: "#00B8D9", border: "1px solid rgba(0,184,217,0.4)", fontWeight: 700 };
    if (status?.includes("Return Pending")) return { backgroundColor: "rgba(255,171,0,0.15)", color: "#FFAB00", border: "1px solid rgba(255,171,0,0.4)", fontWeight: 700 };
    if (status?.includes("Return") || status?.includes("Returned")) return { backgroundColor: "rgba(220,53,69,0.15)", color: "#dc3545", border: "1px solid rgba(220,53,69,0.4)", fontWeight: 700 };
    if (status?.includes("Invoiced")) return { backgroundColor: "rgba(37,99,235,0.15)", color: "#2563EB", border: "1px solid rgba(37,99,235,0.4)", fontWeight: 700 };
    return { backgroundColor: "rgba(253,126,20,0.15)", color: "#fd7e14", border: "1px solid rgba(253,126,20,0.4)", fontWeight: 700 };
  };

  const renderOrderItemStatus = useCallback(
    (row: OrderItemRow): string | JSX.Element => {
      const status = orderItemStatus(row);

      const shouldShowInvoiceLink =
        ["Invoiced", "Applied (Invoiced)", "Returned (Invoiced)", "Return Initiated (Invoiced)", "Return Pending (Invoiced)", "Return Rejected (Invoiced)"].includes(
          status
        ) && row?.mfgInvoiceNumber;

      const statusChip = (
        <Chip
          label={status}
          size="small"
          sx={{
            ...getStatusChipSx(status),
            fontSize: "0.7rem",
            height: 22,
            maxWidth: "100%",
            "& .MuiChip-label": {
              px: 1,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              display: "block",
              lineHeight: "20px",
            },
          }}
        />
      );

      if (shouldShowInvoiceLink) {
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: 0.35,
              minWidth: 0,
              py: 0.1,
            }}
          >
            {statusChip}
            <Link
              component="button"
              underline="hover"
              onClick={() =>
                navigate("/billing_detail", { state: { invoiceId: row.invoiceId } })
              }
              sx={{
                fontSize: "0.72rem",
                fontWeight: 600,
                color: "#42A5F5",
                lineHeight: 1.2,
                p: 0.5,
                m: 0,
                textAlign: "left",
                whiteSpace: "nowrap",
              }}
            >
              ({row.mfgInvoiceNumber})
            </Link>
          </Box>
        );
      }

      return statusChip;
    },
    [orderItemStatus, navigate]
  );
  //CS-1191 - End
  const isAllSelected = useMemo(() => {
    if (orderItemRow?.length === checkedList?.length) {
      return true;
    } else {
      return false;
    }
  }, [checkedList, orderItemRow]);

  const caUpdateOrderClaim = useSelector((state: RootState) =>
    selectIsTokenAvailable("caUpdateOrderClaim")(state)
  );
  const caUpdateOrderInvoice = useSelector((state: RootState) =>
    selectIsTokenAvailable("caUpdateOrderInvoice")(state)
  );

  const handleCheckboxEnableDisable = (row: any) => {
    if (row.claim && caUpdateOrderClaim) {
      if (row.claim && row.claim.claimStatus) {
        return true;
      } else {
        return false;
      }
    } else if (row.invoice && caUpdateOrderInvoice) {
      if (row.invoice && row.invoice.mfgInvoiceStatus) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  };

  const disableCheckBox = (row: OrderItemRow) => {
    const status = orderItemStatus(row);
    const isInvoicedStatus =
      status === "Invoiced" ||
      status === "Applied (Invoiced)" ||
      status === "Returned (Invoiced)" ||
      status === "Return Initiated (Invoiced)" ||
      status === "Return Pending (Invoiced)" ||
      status === "Return Rejected (Invoiced)";

    if (isInvoicedStatus && row.invoiceId !== currentInvoiceId) {
      return true;
    }
    return false;
  };

  const handleDropzoneChange = async (
    uploadedFiles: ExtendedFile[],
    fieldName: any
  ) => {
    if (uploadedFiles && uploadedFiles.length > 0) {
      const base64File = String(await convertBase64(uploadedFiles[0]));
      const fileName = uploadedFiles[0].name;
      const fileExtension = fileName
        .slice(fileName.lastIndexOf(".") + 1)
        .toLowerCase();
      const base64Substring = base64File.substring(
        fileExtension === "pdf"
          ? "data:application/pdf;base64,".length
          : "data:image/png;base64,".length,
        base64File.length
      );
      const attachmentObj: Attachment[] = [
        {
          content: base64Substring,
          documentName: fileName,
          fileType: fileExtension,
          type: "INVOICE",
        },
      ];
      setValue("attachment", attachmentObj);
    }
  };

  const handleDeleteFile = () => {
    setValue("attachment", []);
  };

  // const handleSelectAllCheckbox = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   const { checked } = e.target;
  //   if (checked) {
  //     const selectList: number[] = [];
  //     let invAmt: number = 0;
  //     orderItemRow?.forEach((row:any) => {
  //     const status = orderItemStatus(row);
  //       if (status !== "Invoiced" && status !== "Applied (Invoiced)" && status !== "Returned (Invoiced)") {
  //         selectList.push(row.orderItemId);
  //         invAmt += (row.hasSKUPrice ? row.extendedPrice : row.extendedPrice * row.units);
  //       }
  //     });
  //     setTotalExtendedPrice(invAmt);
  //     setValue("calculatedAmt", invAmt);
  //     setCheckedList(selectList);
  //   } else {
  //     setCheckedList([]);
  //     setTotalExtendedPrice(0);
  //     setValue("calculatedAmt", 0);
  //   }
  // };

  const handleSelectAllCheckbox = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target;

    if (checked) {
      const selectList: number[] = [];
      const unselectList: number[] = [];
      let invAmt: number = 0;

      orderItemRow?.forEach((row: any) => {
        const status = orderItemStatus(row);
        // const shouldSelect = status !== "Invoiced" && status !== "Applied (Invoiced)" && status !== "Returned (Invoiced)";

        if (status) {
          selectList.push(row.orderItemId);
          //CS-156p0 - Adding order item price directly from backend
          invAmt += row.orderItemPrice;
          // invAmt += row.hasSKUPrice
          //   ? row.extendedPrice
          //   : row.extendedPrice * row.units;
        } else {
          unselectList.push(row.orderItemId);
        }
      });

      setCheckedList(selectList);
      setUncheckedList(unselectList);
      setTotalExtendedPrice(invAmt);
      setValue("calculatedAmt", invAmt);
    } else {
      const allIds = orderItemRow?.map((row: any) => row.orderItemId) || [];

      setCheckedList([]);
      setUncheckedList(allIds);
      setTotalExtendedPrice(0);
      setValue("calculatedAmt", 0);
    }
  };

  const handleDatechange = (date: any) => {
    if (date) {
      setValue("invoicedDate", dayjs(date).format("MM/DD/YYYY"));
      setValue(
        "invoiceDueDate",
        dayjs(date).add(invoiceNetdays, "day").format("MM/DD/YYYY")
      );
    }
  };
  //CS-1191-START
  //   const renderCommissionAmountField = (
  //   row: any, index: any
  // ): JSX.Element => {
  //   return (
  //     <>
  //       <CustomCurrencyField
  //         id={`commissionAmount-${index}`}
  //         controllerName={`commissionAmount-${index}`}
  //         control={control}
  //         rules={
  //           row?.agencyType === "V3"
  //             ? {
  //                 required: "V3 Commission Amount is required.",
  //               }
  //             : {}
  //         }
  //       />
  //     </>
  //   );
  // };

  // const handleActionEdit = (row: any, rowIndex: number) => {
  //   setEditCommission(true);
  //   setSelectedAssociateType(row.associateAgencyId ? "AGENCY" : row.associateType)
  //   if (row?.agencyType === "OVERRIDE") {
  //     setIsCommTypeOverride(true);
  //   } else {
  //     setIsCommTypeOverride(false);
  //   }
  //  const isEditing = Boolean(row?.agencyId);
  //  if(isEditing){
  //   setAgencyData({agencyId:row?.agencyId , isEditing:isEditing})
  //  }

  //   const selectedCommission: any = {
  //     productCommissionId: row.productCommissionId,
  //     productId: row.productId === undefined ? undefined : Array.isArray(row.productId) ? row.productId : [row.productId],
  //     productName: row.productName,
  //     associateType:row.associateAgencyId? "AGENCY" :row.associateType,
  //     associateAgencyId: row.associateAgencyId,
  //     associatePracticeId: row.associatePracticeId,
  //     associateAccountRepId: row.associateAccountRepId,
  //     commissionRate: row.commissionRate ? Number(row.commissionRate.toString().split(" ")[0]) : "",
  //     commissionPayTerms: row.commissionPayTerms,
  //     associateAgencyName: row.associateAgencyName,
  //     associatePracticeName: row.associatePracticeName,
  //     associateAccountRepName: row.associateAccountRepName,
  //     associateAgencyCommission: row.associateAgencyCommission,
  //     effectiveDate: row.effectiveDate,
  //     inactiveDate: row.inactiveDate,
  //     rowIndex: row.rowIndex
  //   }
  //   setSelectedCommission(selectedCommission);
  //   setOpen(true);
  // }
  //CS-1191 - Start
  const handleEditCommissionData = (data: any) => {
    if (watch("invoiceAmt") > 0) {
      setShowEditCommissionPopup(true);
      setAgencyData({ ...data, invoiceAmt: getValues("invoiceAmt") });
    } else {
      let alertMessage =
        "Please enter invoice amount before editing service fee data.";
      const alertObj: SingleAlertInfo = {
        message: alertMessage,
        alertType: "error",
      };
      dispatch(setSingleAlertObj(alertObj));
    }
  };
  const handleOvrCommissionPopup = () => {
    if (watch("invoiceAmt") > 0) {
      setShowOvrCommissionPopup(true);
    } else {
      let alertMessage =
        "Please enter invoice amount before adding new ovr service fee data.";
      const alertObj: SingleAlertInfo = {
        message: alertMessage,
        alertType: "error",
      };
      dispatch(setSingleAlertObj(alertObj));
    }
  };
  //CS-1191 - End
  const handleDeleteInvoiceSpecificAgency = (data: any) => {
    if (data?.invoiceAgencyId) {
      cancelOptionPopup();
      dispatch(
        deleteInvSpecificAgencyByInvAgencyId(data?.invoiceAgencyId)
      ).then(async (res) => {
        if (res.payload.message === "SUCCESS") {
          editInvoiceDetail?.invoiceId && await dispatch(getEditInvDetailsById(editInvoiceDetail.invoiceId));
        }
      });
    } else {
      setCombinedCommissionData((prevData) =>
        prevData.filter((item) => item.agencyId !== data.agencyId)
      );
      setNewOvrCommissionData((prevData: any) =>
        prevData.filter((item: any) => item.agencyId !== data.agencyId)
      );
      cancelOptionPopup();
    }
  };
  const openDeletePopupForAgency = (row: any) => {
    setShowOptionsPopup({
      message: `${row?.agencyName}'s service fee will be deleted. Do you wish to proceed?`,
      show: true,
      variant: "warning",
      buttons: [
        {
          name: "Yes",
          color: "primary",
          onClick: () => handleDeleteInvoiceSpecificAgency(row),
        },
        { name: "No", color: "error", onClick: cancelOptionPopup },
      ],
    });
  };
  const commissionActionButtons = (row: any, index: any): JSX.Element => {
    return (
      <>
        <CustomIconButton
          color="info"
          size="small"
          variant="outlined"
          tooltipMessage="Edit"
          tooltipPlacement="top"
          onClick={() => handleEditCommissionData(row)} //handleActionEdit(row, index)
          style={{
            display: hideElement(row?.productCommissionId ? false : true),
          }}
        >
          <Edit fontSize="small" />
        </CustomIconButton>
        <CustomIconButton
          color="error"
          size="small"
          variant="outlined"
          tooltipMessage="Delete"
          tooltipPlacement="top"
          onClick={() => {
            openDeletePopupForAgency(row);
          }}
          style={{
            display: hideElement(
              row?.productCommissionId === null &&
                row?.agencyType === "OVERRIDE"
                ? false
                : true
            ),
          }}
        >
          <Delete fontSize="small" />
        </CustomIconButton>
        {row?.agencyType !== "OVERRIDE" && (
          <CustomIconButton
            color="success"
            size="small"
            variant="outlined"
            tooltipMessage="Add"
            tooltipPlacement="top"
            onClick={() => {
              navigate("/agency_form", {
                state: { agency: { agencyId: row.agencyId } },
              });
            }}
            style={{
              display: hideElement(row?.productCommissionId !== null),
            }}
          >
            <Add fontSize="small" />
          </CustomIconButton>
        )}
      </>
    );
  };

  useEffect(() => {
    // CS-2046 - P&B invoices never show/select V3 agency in the commission section
    const isPurchaseAndBill = editInvoiceDetail?.purchaseAndBill === "Y";
    const apiData = (editInvoiceDetail?.invoiceCommissionDetailsList ?? []).filter(
      (item: any) => !(isPurchaseAndBill && isV3CommissionType(allCodeTablesValuesMap, item?.agencyType))
    );

    const newEntries = Array.isArray(newOvrCommissionData)
      ? newOvrCommissionData
      : [];
    const specificUpdates = Array.isArray(newInvSpecificCommissionData)
      ? newInvSpecificCommissionData
      : [];

    // Filter out any items that do not have a valid productCommissionId
    const validUpdates = [...specificUpdates].filter(
      (item) => item.productCommissionId != null
    );

    const updatesById = validUpdates.reduce((acc, item) => {
      acc[item.productCommissionId] = item;
      return acc;
    }, {} as Record<string | number, any>);

    // Replace specific items, only if there's a valid update
    const merged = apiData.map((item) => {
      const updated = updatesById[item.productCommissionId];
      if (item.productCommissionId != null && updated) {
        return {
          ...updated,
          originalAgencyId: updated.originalAgencyId ?? item.agencyId,
        };
      }
      return { ...item, originalAgencyId: item.agencyId };
    });

    // Add new entries (OVR + Specific) which are not in the original data
    const newOnly = [...newEntries, ...specificUpdates].filter(
      (item) =>
        !(isPurchaseAndBill && isV3CommissionType(allCodeTablesValuesMap, item?.agencyType)) &&
        (!item.productCommissionId ||
          !apiData.some(
            (orig) => orig.productCommissionId === item.productCommissionId
          ))
    );

    setCombinedCommissionData([...merged, ...newOnly]);
  }, [editInvoiceDetail, newOvrCommissionData, newInvSpecificCommissionData]);

  //CS-1191 - END
  const orderGraftColumns: Column[] = [
    {
      id: "checkBox",
      label: (
        <CustomCheckbox
          control={control}
          onChange={(e) => handleSelectAllCheckbox(e)}
          controllerName="selectAll"
          checked={isAllSelected || false}
        />
      ),
    },
    { id: "orderSize", label: "Ordered Size", minWidth: 100 },
    { id: "serialNumber", label: "Serial Number", minWidth: 100 },
    { id: "status", label: "Status", minWidth: 100 },
    { id: "cogs", label: "COGs", minWidth: 100 },
  ];

  const commissionSummaryRows: any[] = useMemo(() => {
    const rows: any[] = [];
    combinedCommissionData.forEach((item: any, index: any) => {
      //CS-1213 - Start
      const commAMount = calculateCommissionAmount(
        Number(getValues("invoiceAmt")),
        Number(item.commissionRate)
      );
      const rate = calculateCommissionRate(
        Number(getValues("invoiceAmt")),
        Number(commAMount)
      );

      // if(getValues('invoiceAmt')){
      const row = {
        id: item.productCommissionId,
        type: getCodeTableDisplayLabel(
          allCodeTablesValuesMap,
          COMMISSION_TYPE_CODETABLE,
          item.agencyType
        ),
        agency:
          item.productCommissionId && item.agencyType === "OVERRIDE"
            ? `${item.agencyName} (${item.associateType})`
            : item.agencyName,
        commissionPercent: (
          <span style={{ cursor: "pointer" }} title={rate.toString()}>
            {formatValue(rate, { suffix: "%" })}
          </span>
        ), // item.commissionRate !== null ? `${item.commissionRate} %` : '',
        commissionAmount: (
          <span style={{ cursor: "pointer" }} title={commAMount.toString()}>
            {formatValue(commAMount, { prefix: "$" })}
          </span>
        ), // commissionAmount: formatValue(commAMount, { prefix: '$' }),//priceFormatter(item?.commissionAmt || calculateCommissionAmount(getValues('invoiceAmt'), Number(item.commissionRate))),
        actionButtons: commissionActionButtons(item, index),
      };
      //CS-1213 - End
      setValue(
        `commissionAmt-${index}`,
        item?.commissionAmt ||
        calculateCommissionAmount(
          getValues("invoiceAmt"),
          Number(item.commissionRate)
        )
      );
      rows.push(row);
      // }else{
      //     const row = {
      //       id: item.productCommissionId,
      //       type: item.agencyType,
      //       agency: item.productCommissionId && item.agencyType === "OVERRIDE" ? `${item.agencyName} (${item.associateType})` : item.agencyName,
      //       commissionPercent: item.commissionRate !== null ? `${item.commissionRate} %` : '',
      //       commissionAmount:priceFormatter(item?.commissionAmt || calculateCommissionAmount(getValues('invoiceAmt'), Number(item.commissionRate))),
      //       actionButtons: commissionActionButtons(item, index),
      //   };
      //   setValue(`commissionAmt-${index}`, item?.commissionAmt || calculateCommissionAmount(getValues('invoiceAmt'), Number(item.commissionRate)))
      //   rows.push(row);
      // }
    });
    return rows;
  }, [combinedCommissionData, watch("invoiceAmt"), allCodeTablesValuesMap]);

  const orderGraftRows: any[] = useMemo(() => {
    const rows: any[] = [];
    orderItemRow &&
      orderItemRow?.map((item: any, index: any) => {
        const row = {
          checkBox: (
            <CustomCheckbox
              onChange={(e) =>
                handleCheckboxChange(
                  e,
                  item.orderItemId,
                  item.extendedPrice,
                  item.units,
                  item.hasSKUPrice,
                  item.orderItemPrice,//CS-1560
                )
              }
              control={control}
              name={`select-${item.orderItemId}`}
              checked={checkedList.includes(item.orderItemId)}
              isDisabled={disableCheckBox(item)}
            />
          ),
          orderSize: item.size,
          serialNumber: item.serialNumber,
          status: renderOrderItemStatus(item), //orderItemStatus(item),//CS-1191
          //CS-1560 - Removing the calculation now it will be directly fetched from backend
          cogs: priceFormatter(
            item.orderItemPrice),
          // cogs: priceFormatter(
          //   item.hasSKUPrice
          //     ? item.extendedPrice
          //     : item.extendedPrice * item.units
          // ),
        };
        rows.push(row);
      });
    return rows;
  }, [orderItemRow, checkedList]);

  const handleClose = () => {
    setShowOvrCommissionPopup(false);
    setShowEditCommissionPopup(false);
    setAgencyData({});
  };

  //CS-1191-END

  useEffect(() => () => {
    dispatch(resetEditInvoiceDetails());
  }, []);

  
  const invoiceAmt = Number(watch("invoiceAmt") || 0);
  const shippingCharge = Number(watch("shippingCharge") || 0);
  const lateFee = Number(watch("lateFee") || 0);
  const totalAmount = invoiceAmt + shippingCharge + lateFee;
  const originalShippingCharge = Number(editInvoiceDetail?.shippingCharge ?? 0);
  const orderShippingCharge = resolvePreviousShippingCharge(orderDetail);
  const hasShippingChargeChanged = !shippingChargesEqual(
    shippingCharge,
    originalShippingCharge
  );
  const showShippingChargeWarning =
    Boolean(orderDetail) &&
    hasShippingChargeChanged &&
    !shippingChargesEqual(shippingCharge, orderShippingCharge);

  const FieldLabel = ({ label, required = false }: { label: string; required?: boolean }) => (
    <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: mTokens.textSecondary, mb: 0.7, display: "flex", alignItems: "center", gap: 0.4 }}>
      {label}{required && <Box component="span" sx={{ color: mTokens.accent }}>*</Box>}
    </Typography>
  );

  const isPurchaseAndBillInvoice = editInvoiceDetail?.purchaseAndBill === "Y";
  const invoiceReferenceIdField = isPurchaseAndBillInvoice ? (
    <Grid item xs={12} md={6}>
      <FieldLabel label="Invoice Reference Id" />
      <Box sx={inputFieldSx()}>
        <CustomTextField
          controllerName="invReferenceId"
          control={control}
          label=""
          size="small"
          maxLength={100}
        />
      </Box>
    </Grid>
  ) : null;

  const highlightFieldRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    if (!highlightFieldName) return;
    const el = highlightFieldRefs.current[highlightFieldName];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [highlightFieldName]);

  const highlightFieldSx = (fieldName: string): Record<string, unknown> =>
    highlightFieldName === fieldName
      ? {
          "@keyframes highlightFieldRadar": {
            "0%": { boxShadow: `0 0 0 0 ${mTokens.accent}99` },
            "100%": { boxShadow: `0 0 0 12px ${mTokens.accent}00` },
          },
          "& .MuiOutlinedInput-root": {
            animation: "highlightFieldRadar 1.6s ease-out infinite",
          },
          "& .MuiOutlinedInput-root fieldset, & .MuiOutlinedInput-notchedOutline": {
            borderColor: `${mTokens.accent} !important`,
            borderWidth: "2px !important",
          },
        }
      : {};

  const InfoRow = ({ label, value }: { label: string; value?: React.ReactNode }) => (
    <Box display="flex" alignItems="center" py={0.8} sx={{ borderBottom: `1px solid ${mTokens.border}`, "&:last-child": { borderBottom: "none" } }}>
      <Typography sx={{ fontSize: "0.82rem", color: mTokens.textSecondary, width: "40%", flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: "0.88rem", color: mTokens.textPrimary, fontWeight: 600, flex: 1 }} component="div">
        {value ?? "—"}
      </Typography>
    </Box>
  );

  const colHeaderSx = {
    fontSize: "0.68rem",
    fontWeight: 700,
    color: mTokens.textSecondary,
    textTransform: "uppercase" as const,
    letterSpacing: "0.08em",
  };
  const colBodySx = { fontSize: "0.88rem", color: mTokens.textPrimary, fontWeight: 500 };
  const graftGridCols = "48px minmax(90px, 1fr) minmax(110px, 1.2fr) minmax(120px, 1.4fr) minmax(80px, 0.9fr)";
  const commGridCols = "minmax(70px, 0.8fr) minmax(120px, 1.4fr) minmax(70px, 0.7fr) minmax(90px, 0.9fr) minmax(90px, 0.8fr)";
  const commissionActionBtnSx = {
    display: "flex",
    alignItems: "center",
    gap: 0.8,
    px: 1.4,
    height: 32,
    border: "1px solid rgba(20,184,166,0.55)",
    borderRadius: "10px",
    backgroundColor: "transparent",
    color: "#14b8a6",
    cursor: "pointer",
    fontSize: "0.78rem",
    fontWeight: 700,
    letterSpacing: "0.04em",
    transition: "all 0.2s",
    "&:hover": { backgroundColor: "rgba(20,184,166,0.1)" },
    "& svg": { fontSize: "0.95rem" },
  };

  if (compact) {
    return (
      <>
        {showOptionPopup.show && (
          <OptionsPopup
            open={showOptionPopup.show}
            onClose={() => setShowOptionsPopup({ message: "", show: false })}
            variant={showOptionPopup.variant}
            message={<>{showOptionPopup.message}</>}
            buttons={showOptionPopup.buttons}
            title={showOptionPopup.title}
            obj={showOptionPopup.obj}
          />
        )}
        <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <FieldLabel label="MFG Invoice #" required />
          <Box sx={inputFieldSx()}>
            <CustomTextField controllerName="mfgInvoice" control={control} label="" size="small" disabled />
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <FieldLabel label="Order #" />
          <Box sx={inputFieldSx()}>
            <CustomTextField controllerName="orderNumber" control={control} label="" size="small" disabled />
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <FieldLabel label="Calculated Amt" />
          <Box sx={inputFieldSx()}>
            <CustomCurrencyField label="" controllerName="calculatedAmt" control={control} isDisabled size="small" />
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <FieldLabel label="Invoice Amt" required />
          <Box sx={inputFieldSx(!!errors.invoiceAmt)}>
            <CustomCurrencyField
              controllerName="invoiceAmt"
              control={control}
              label=""
              size="small"
              rules={{
                required: "Invoice amount is required",
                validate: (value: any) => parseFloat(value) > 0 || "Invoice amount must be greater than 0",
              }}
            />
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <FieldLabel label="Shipping Charge" />
          <Box
            ref={(el: HTMLDivElement | null) => (highlightFieldRefs.current.shippingCharge = el)}
            sx={[inputFieldSx(), highlightFieldSx("shippingCharge")]}
          >
            <CustomCurrencyField controllerName="shippingCharge" control={control} label="" size="small" />
          </Box>
        </Grid>
        {showShippingChargeWarning && (
          <Grid item xs={12}>
            <Typography sx={{ fontSize: "0.75rem", color: "warning.main" }}>
              The shipping charge entered on this invoice does not match the order shipping charge. Updating the invoice may result in a discrepancy between the order and invoice totals.
            </Typography>
          </Grid>
        )}
        <Grid item xs={12} md={6}>
          <FieldLabel label="Late Fee" />
          <Box
            ref={(el: HTMLDivElement | null) => (highlightFieldRefs.current.lateFee = el)}
            sx={[inputFieldSx(), highlightFieldSx("lateFee")]}
          >
            <CustomCurrencyField controllerName="lateFee" control={control} label="" size="small" />
          </Box>
        </Grid>
        {invoiceReferenceIdField}
        <Grid item xs={12}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            px={2}
            py={1.4}
            borderRadius="12px"
            sx={{ backgroundColor: "rgba(16,185,129,0.1)", border: "1px solid rgba(16,185,129,0.35)" }}
          >
            <Typography sx={{ fontSize: "0.9rem", fontWeight: 700, color: mTokens.textPrimary }}>Total</Typography>
            <Typography sx={{ fontSize: "1.15rem", fontWeight: 800, color: "#10B981" }}>
              {priceFormatter(totalAmount)}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <FieldLabel label="Invoiced Date" required />
          <Box
            ref={(el: HTMLDivElement | null) => (highlightFieldRefs.current.invoicedDate = el)}
            sx={[datepickerWrapSx(!!errors.invoicedDate), highlightFieldSx("invoicedDate")]}
          >
            <CustomDatePicker
              controllerName="invoicedDate"
              control={control}
              label=""
              size="small"
              rules={{ required: "Invoice date is required" }}
              onChange={handleDatechange}
              popperZIndex={1700}
            />
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <FieldLabel label="Invoice Due Date" />
          <Box
            ref={(el: HTMLDivElement | null) => (highlightFieldRefs.current.invoiceDueDate = el)}
            sx={[datepickerWrapSx(), highlightFieldSx("invoiceDueDate")]}
          >
            <CustomDatePicker controllerName="invoiceDueDate" control={control} label="" size="small" popperZIndex={1700} />
          </Box>
        </Grid>
        </Grid>
      </>
    );
  }

  return (
    <>
      {showOvrCommissionPopup && (
        <OVRCommissionPopup
          open={showOvrCommissionPopup}
          handleClose={handleClose}
          onAddOvrCommission={(commission) => {
            setNewOvrCommissionData((prevCommission: any) => [
              ...prevCommission,
              commission,
            ]);
          }}
          agencyData={combinedCommissionData}
        />
      )}
      {showEditCommissionPopup && (
        <EditCommissionPopup
          open={showEditCommissionPopup}
          handleClose={handleClose}
          onAddInvSpecificCommission={(commission) => {
            setNewInvSpecificCommissionData((prevCommission: any) => [
              ...prevCommission,
              commission,
            ]);
          }}
          agencyData={agencyData}
        />
      )}
      <Box
        sx={
          hideHeader
            ? { pb: "8px" }
            : { mx: { xs: "10px", sm: "20px" }, mt: "12px", pb: "72px" }
        }
      >
        {showOptionPopup.show && (
          <OptionsPopup
            open={showOptionPopup.show}
            onClose={() => setShowOptionsPopup({ message: "", show: false })}
            variant={showOptionPopup.variant}
            message={<>{showOptionPopup.message}</>}
            buttons={showOptionPopup.buttons}
            title={showOptionPopup.title}
            obj={showOptionPopup.obj}
          />
        )}

        {!hideHeader && (
          <Box mb={1.5} display="flex" alignItems="flex-start" gap={1.5}>
            <PageBackButton
              tooltip={null}
              onClick={() => {
                closeOrGoBack();
                setInvoiceData(null);
                reset();
              }}
            />
            <Box data-testid="header">
              <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: mTokens.textSecondary, textTransform: "uppercase", letterSpacing: "0.12em", mb: 0.3 }}>
                Billing / Update Invoice
              </Typography>
              <Typography sx={{ fontSize: "1.6rem", fontWeight: 800, color: mTokens.textPrimary, lineHeight: 1.1 }}>
                Update Invoice
              </Typography>
              <Typography sx={{ fontSize: "0.78rem", color: mTokens.textSecondary, mt: 0.4 }}>
                Review selected grafts, service fees, and update invoice details.
              </Typography>
            </Box>
          </Box>
        )}

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "2fr 1fr" }, gap: 1.5, alignItems: "flex-start" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, order: { xs: 1, md: 1 } }}>
            <Box sx={cardSx} data-testid="custom-table-graft">
              <SectionHeader icon={<Inventory2Outlined fontSize="small" />} title="Grafts" />
              <Box>
                <span data-testid="row-count-graft" style={{ display: "none" }}>{orderGraftRows.length}</span>
                {isNarrow ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 2, py: 0.8, borderBottom: `1px solid ${mTokens.border}`, backgroundColor: mTokens.headerFooterBg }}>
                    <CustomCheckbox
                      control={control}
                      onChange={(e) => handleSelectAllCheckbox(e)}
                      controllerName="selectAll"
                      checked={isAllSelected}
                    />
                    <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: mTokens.textSecondary }}>
                      Select all available
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: "grid", gridTemplateColumns: graftGridCols, gap: 1, px: 2.8, py: 0.8, borderBottom: `1px solid ${mTokens.border}`, backgroundColor: mTokens.headerFooterBg }}>
                    <Box>
                      <CustomCheckbox
                        control={control}
                        onChange={(e) => handleSelectAllCheckbox(e)}
                        controllerName="selectAll"
                        checked={isAllSelected}
                      />
                    </Box>
                    <Typography sx={colHeaderSx}>Ordered Size</Typography>
                    <Typography sx={colHeaderSx}>Serial Number</Typography>
                    <Typography sx={colHeaderSx}>Status</Typography>
                    <Typography sx={{ ...colHeaderSx, textAlign: "right" }}>COGs</Typography>
                  </Box>
                )}
                {orderGraftRows.length === 0 ? (
                  <Box sx={{ px: 2.8, py: 3, textAlign: "center" }}>
                    <Typography sx={{ fontSize: "0.88rem", color: mTokens.textSecondary }}>No grafts available</Typography>
                  </Box>
                ) : (
                  orderGraftRows.map((row: any, index: number) => {
                    const rowBorder = index < orderGraftRows.length - 1 ? `1px dashed ${mTokens.border}` : "none";
                    return (
                      <Box key={`graft-${index}`} data-testid={`row-graft-${index}`}>
                        {isNarrow ? (
                          <Box sx={{ px: 2, py: 1.2, borderBottom: rowBorder }}>
                            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2 }}>
                              <Box sx={{ pt: 0.2, flexShrink: 0 }}>{row.checkBox}</Box>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1, mb: 0.4 }}>
                                  <Typography sx={{ fontSize: "0.9rem", fontWeight: 700, color: mTokens.textPrimary }} data-testid={`size-graft-${index}`}>
                                    {row.orderSize}
                                  </Typography>
                                  <Typography sx={{ fontSize: "0.88rem", fontWeight: 700, color: mTokens.accent, flexShrink: 0 }} data-testid={`cogs-graft-${index}`}>
                                    {row.cogs}
                                  </Typography>
                                </Box>
                                {row.serialNumber ? (
                                  <Typography sx={{ fontSize: "0.78rem", color: mTokens.textSecondary, mb: 0.5 }} data-testid={`serial-graft-${index}`}>
                                    SN: {row.serialNumber}
                                  </Typography>
                                ) : (
                                  <span data-testid={`serial-graft-${index}`} />
                                )}
                                <Box data-testid={`status-graft-${index}`}>{row.status}</Box>
                              </Box>
                            </Box>
                          </Box>
                        ) : (
                          <Box sx={{ display: "grid", gridTemplateColumns: graftGridCols, gap: 1, px: 2.8, py: 1.2, borderBottom: rowBorder, alignItems: "flex-start" }}>
                            <Box sx={{ pt: 0.2 }}>{row.checkBox}</Box>
                            <Typography sx={{ ...colBodySx, pt: 0.35 }} data-testid={`size-graft-${index}`}>{row.orderSize}</Typography>
                            <Typography sx={{ ...colBodySx, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", pt: 0.35 }} data-testid={`serial-graft-${index}`}>
                              {row.serialNumber || "—"}
                            </Typography>
                            <Box sx={{ minWidth: 0 }} data-testid={`status-graft-${index}`}>{row.status}</Box>
                            <Typography sx={{ ...colBodySx, fontWeight: 700, color: mTokens.accent, textAlign: "right", pt: 0.35 }} data-testid={`cogs-graft-${index}`}>
                              {row.cogs}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    );
                  })
                )}
              </Box>
            </Box>

            {editInvoiceDetail !== null && (
              <Box sx={cardSx} data-testid="custom-table-comm">
                <SectionHeader
                  icon={<AccountBalanceWalletOutlined fontSize="small" />}
                  title="Service Fees"
                  action={
                    <Box
                      component="button"
                      type="button"
                      data-testid="btn-OVR Commission"
                      onClick={() => handleOvrCommissionPopup()}
                      sx={commissionActionBtnSx}
                    >
                      <AddCircleOutlineOutlined sx={{ fontSize: "0.95rem" }} />
                      OVR Service Fee
                    </Box>
                  }
                />
                <Box>
                  <span data-testid="row-count-comm" style={{ display: "none" }}>{commissionSummaryRows.length}</span>
                  {!isNarrow && (
                    <Box sx={{ display: "grid", gridTemplateColumns: commGridCols, gap: 1, px: 2.8, py: 0.8, borderBottom: `1px solid ${mTokens.border}`, backgroundColor: mTokens.headerFooterBg }}>
                      <Typography sx={colHeaderSx}>Type</Typography>
                      <Typography sx={colHeaderSx}>Agency</Typography>
                      <Typography sx={{ ...colHeaderSx, textAlign: "right" }}>Rate (%)</Typography>
                      <Typography sx={{ ...colHeaderSx, textAlign: "right" }}>Amount ($)</Typography>
                      <Typography sx={{ ...colHeaderSx, textAlign: "center" }}>Action</Typography>
                    </Box>
                  )}
                  {commissionSummaryRows.length === 0 ? (
                    <Box sx={{ px: 2.8, py: 3, textAlign: "center" }}>
                      <Typography sx={{ fontSize: "0.88rem", color: mTokens.textSecondary }}>No service fees found</Typography>
                    </Box>
                  ) : (
                    commissionSummaryRows.map((row: any, index: number) => {
                      const rowBorder = index < commissionSummaryRows.length - 1 ? `1px dashed ${mTokens.border}` : "none";
                      return (
                        <Box key={`comm-${index}`} data-testid={`row-comm-${index}`}>
                          {isNarrow ? (
                            <Box sx={{ px: 2, py: 1.2, borderBottom: rowBorder }}>
                              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1, mb: 0.4 }}>
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography sx={{ fontSize: "0.9rem", fontWeight: 700, color: mTokens.textPrimary }} data-testid={`agency-comm-${index}`}>
                                    {row.agency}
                                  </Typography>
                                  <Typography sx={{ fontSize: "0.75rem", color: mTokens.textSecondary, mt: 0.2 }} data-testid={`type-comm-${index}`}>
                                    {row.type}
                                  </Typography>
                                </Box>
                                <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>{row.actionButtons}</Box>
                              </Box>
                              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 0.6 }}>
                                <Typography sx={{ fontSize: "0.78rem", color: mTokens.textSecondary }} data-testid={`rate-comm-${index}`}>
                                  Rate: <Box component="span" sx={{ fontWeight: 700, color: mTokens.textPrimary }}>{row.commissionPercent}</Box>
                                </Typography>
                                <Typography sx={{ fontSize: "0.88rem", fontWeight: 700, color: mTokens.textPrimary }} data-testid={`amount-comm-${index}`}>
                                  {row.commissionAmount}
                                </Typography>
                              </Box>
                            </Box>
                          ) : (
                            <Box sx={{ display: "grid", gridTemplateColumns: commGridCols, gap: 1, px: 2.8, py: 1.2, borderBottom: rowBorder, alignItems: "center" }}>
                              <Typography sx={colBodySx} data-testid={`type-comm-${index}`}>{row.type}</Typography>
                              <Typography sx={{ ...colBodySx, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} data-testid={`agency-comm-${index}`}>
                                {row.agency}
                              </Typography>
                              <Typography sx={{ ...colBodySx, textAlign: "right" }} data-testid={`rate-comm-${index}`}>{row.commissionPercent}</Typography>
                              <Typography sx={{ ...colBodySx, fontWeight: 700, textAlign: "right" }} data-testid={`amount-comm-${index}`}>{row.commissionAmount}</Typography>
                              <Box sx={{ display: "flex", justifyContent: "center", gap: 0.5 }}>{row.actionButtons}</Box>
                            </Box>
                          )}
                        </Box>
                      );
                    })
                  )}
                </Box>
              </Box>
            )}
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, order: { xs: 2, md: 2 } }}>
            <Box sx={cardSx}>
              <SectionHeader icon={<PersonOutlined fontSize="small" />} title="Details" />
              <Box sx={{ px: 2.8, py: 1.5 }}>
                <InfoRow label="Patient" value={invoiceData?.patientName} />
                <InfoRow label="Product" value={invoiceData ? `${invoiceData.productName} (${invoiceData.mfgName})` : undefined} />
                <InfoRow label="Order ID" value={invoiceData?.orderNumber} />
              </Box>
            </Box>

            <Box sx={cardSx}>
              <SectionHeader icon={<ReceiptLongOutlined fontSize="small" />} title="Invoice Details" />
              <Box sx={{ px: 2.8, pt: 1.8, pb: 2.8 }}>
                <form onSubmit={handleSubmit(onSubmit)}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <FieldLabel label="MFG Invoice #" required />
                      <Box sx={inputFieldSx(!!errors.mfgInvoice)}>
                        <CustomTextField
                          controllerName="mfgInvoice"
                          control={control}
                          label=""
                          size="small"
                          rules={
                            submitAction === "markInvoiced"
                              ? {
                                required:
                                  "Manufacturer Invoice Number is required",
                              }
                              : {}
                          }
                          disabled={editInvoiceDetail?.purchaseAndBill === "Y"}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FieldLabel label="Calculated Amt" />
                      <Box sx={inputFieldSx()}>
                        <CustomCurrencyField
                          label=""
                          controllerName="calculatedAmt"
                          control={control}
                          isDisabled
                          size="small"
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FieldLabel label="Invoice Amt" required />
                      <Box sx={inputFieldSx(!!errors.invoiceAmt)}>
                        <CustomCurrencyField
                          controllerName="invoiceAmt"
                          control={control}
                          label=""
                          size="small"
                          rules={
                            submitAction === "markInvoiced"
                              ? {
                                required: "Invoice amount is required",
                                validate: (value: any) =>
                                  parseFloat(value) > 0 ||
                                  "Invoice amount must be greater than 0",
                              }
                              : {
                                validate: (value: any) =>
                                  parseFloat(value) > 0 ||
                                  "Invoice amount must be greater than 0",
                              }
                          }
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FieldLabel label="Shipping Charge" />
                      <Box sx={inputFieldSx()}>
                        <CustomCurrencyField
                          controllerName="shippingCharge"
                          control={control}
                          label=""
                          size="small"
                        />
                      </Box>
                    </Grid>
                    {showShippingChargeWarning && (
                      <Grid item xs={12}>
                        <Typography sx={{ fontSize: "0.75rem", color: "warning.main" }}>
                          The shipping charge entered on this invoice does not match the order shipping charge. Updating the invoice may result in a discrepancy between the order and invoice totals.
                        </Typography>
                      </Grid>
                    )}
                    <Grid item xs={12} md={6}>
                      <FieldLabel label="Late Fee" />
                      <Box sx={inputFieldSx()}>
                        <CustomCurrencyField
                          controllerName="lateFee"
                          control={control}
                          label=""
                          size="small"
                        />
                      </Box>
                    </Grid>
                    {invoiceReferenceIdField}
                    <Grid item xs={12}>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        px={2}
                        py={1.4}
                        borderRadius="12px"
                        sx={{
                          backgroundColor: "rgba(16,185,129,0.1)",
                          border: "1px solid rgba(16,185,129,0.35)",
                        }}
                      >
                        <Typography sx={{ fontSize: "0.9rem", fontWeight: 700, color: mTokens.textPrimary }}>
                          Total
                        </Typography>
                        <Typography sx={{ fontSize: "1.15rem", fontWeight: 800, color: "#10B981" }}>
                          {priceFormatter(totalAmount)}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FieldLabel label="Invoiced Date" required />
                      <Box sx={datepickerWrapSx(!!errors.invoicedDate)}>
                        <CustomDatePicker
                          controllerName="invoicedDate"
                          control={control}
                          label=""
                          size="small"
                          rules={
                            submitAction === "markInvoiced"
                              ? { required: "Invoice date is required" }
                              : {}
                          }
                          onChange={handleDatechange}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FieldLabel label="Invoice Due Date" />
                      <Box sx={datepickerWrapSx()}>
                        <CustomDatePicker
                          controllerName="invoiceDueDate"
                          control={control}
                          label=""
                          size="small"
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <FieldLabel label="Comments" />
                      <Box sx={inputFieldSx()}>
                        <CustomTextField
                          control={control}
                          controllerName="comments"
                          label=""
                          size="small"
                          multiline
                          rows={3}
                        />
                      </Box>
                    </Grid>
                    {editInvoiceDetail !== null && editInvoiceDetail?.purchaseAndBill !== null && editInvoiceDetail?.purchaseAndBill !== "Y" && (
                      <Grid item xs={12}>
                        <Dropzone
                          name="invoiceAttachment"
                          onFileChange={() => { }}
                          onFileChangeTest={handleDropzoneChange}
                          onDeleteFile={handleDeleteFile}
                          control={control}
                          maxFileCount={1}
                          customDropzoneText="Upload Invoice PDF"
                        />
                      </Grid>
                    )}
                    {!hideHeader && (
                      <Grid item xs={12}>
                        <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} gap={1.2}>
                          <Button
                            fullWidth
                            size="large"
                            startIcon={<CheckCircleOutline />}
                            type="submit"
                            removeUpperCase
                            onClick={() => {
                              submitActionRef.current = "markInvoiced";
                              setSubmitAction("markInvoiced");
                            }}
                            sx={{
                              ...primaryBtnSx,
                              flex: { sm: 1 },
                              backgroundColor: mTokens.accent,
                              boxShadow: "0 4px 18px rgba(20,184,166,.35)",
                              "& .MuiTypography-root": { color: "#ffffff !important" },
                              "& .MuiButton-startIcon": { color: "#ffffff" },
                            }}
                          >
                            Update Invoice
                          </Button>
                          <Button
                            fullWidth
                            color="error"
                            variant="outlined"
                            removeUpperCase
                            startIcon={<Cancel />}
                            onClick={() => {
                              closeOrGoBack();
                              setInvoiceData(null);
                              reset();
                            }}
                            sx={{
                              ...cancelBtnSx,
                              flex: { sm: 1 },
                              "& .MuiTypography-root": { color: `${mTokens.error} !important` },
                              "& .MuiButton-startIcon": { color: mTokens.error },
                            }}
                          >
                            Cancel
                          </Button>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </form>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
});

export default EditInvoice;
